/**
 * SeededRandom - Générateur de nombres aléatoires avec seed
 * Utilise l'algorithme Mulberry32 pour un PRNG rapide et de qualité
 */

export class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  /**
   * Génère un nombre aléatoire entre 0 et 1
   */
  random(): number {
    let t = (this.seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /**
   * Génère un nombre entier aléatoire entre min (inclus) et max (exclus)
   */
  randomInt(min: number, max: number): number {
    return Math.floor(this.random() * (max - min)) + min;
  }

  /**
   * Génère un nombre aléatoire entre min (inclus) et max (exclus)
   */
  randomFloat(min: number, max: number): number {
    return this.random() * (max - min) + min;
  }
}

/**
 * Crée un seed basé sur le temps, la force du geste et un compteur optionnel
 * Utilise performance.now() pour plus de précision que Date.now()
 */
export function createSeedFromTimeAndForce(force?: number, counter?: number): number {
  // Utiliser performance.now() pour plus de précision (microsecondes)
  const time = performance.now();
  const timePart = Math.floor(time * 1000); // Convertir en millisecondes entières avec précision
  const forcePart = force ? Math.floor(force * 1000) : 0;
  // ⚠️ CRITIQUE : Le counter est maintenant toujours fourni (compteur global)
  // Multiplier par un très grand nombre pour garantir l'impact même avec des lancers très rapides
  const counterPart = counter ? (counter * 10000000) : 0; // Augmenté pour plus d'impact
  // Ajouter un peu d'entropie avec Math.random() pour garantir l'unicité
  const entropy = Math.floor(Math.random() * 1000000);
  // Combiner tous les éléments pour un seed vraiment unique
  // Utiliser des opérations bitwise pour bien mélanger
  let seed = timePart;
  seed = seed ^ (forcePart << 16);
  seed = seed ^ (counterPart << 8);
  seed = seed ^ entropy;
  seed = seed ^ (Math.floor(Math.random() * 1000) << 24); // Encore plus d'entropie
  // ⚠️ CRITIQUE : Ajouter le counter directement pour garantir l'unicité même si le temps est identique
  seed = seed + (counter || 0) * 7919; // Multiplier par un nombre premier pour plus de dispersion
  return seed;
}

/**
 * Instance globale de SeededRandom (initialisée avec le temps)
 */
let globalSeededRandom: SeededRandom | null = null;

/**
 * Compteur global qui s'incrémente à chaque lancer pour garantir l'unicité du seed
 * Même si performance.now() donne le même timestamp, le compteur garantit l'unicité
 */
let globalDiceRollCounter = 0;

/**
 * Réinitialise le générateur global avec un nouveau seed
 * ⚠️ CRITIQUE : Incrémente le compteur global pour garantir l'unicité même avec des lancers rapides
 */
export function resetGlobalSeed(seed?: number): void {
  // Incrémenter le compteur global AVANT de créer le seed
  globalDiceRollCounter++;
  
  // Si un seed est fourni, l'utiliser directement
  // Sinon, créer un seed avec le compteur pour garantir l'unicité
  const finalSeed = seed ?? createSeedFromTimeAndForce(undefined, globalDiceRollCounter);
  
  globalSeededRandom = new SeededRandom(finalSeed);
  console.log(`[SeededRandom] Seed réinitialisé: ${finalSeed}, compteur: ${globalDiceRollCounter}`);
}

/**
 * Obtient le générateur global (l'initialise si nécessaire)
 */
export function getGlobalSeededRandom(): SeededRandom {
  if (!globalSeededRandom) {
    resetGlobalSeed();
  }
  return globalSeededRandom!;
}
